import { CampaignSteps } from '@shared/types/campaigns';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import React, { Dispatch, Fragment, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'next-i18next';
import styles from './CarRegistrationAccordion.module.css';
import Button from '@components/Button';
import CampaignLogin from '../CampaignAccordion/CampaignLogin/CampaignLogin';
import LicensePlateInput from '@components/LicensePlateInput/LicensePlateInput';
import CampaignSignUp from '../CampaignAccordion/CampaignSignUp/CampaignSignUp';
import AdyenDropin from '@components/Adyen';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import MultiLicensePlateInput from '@components/LicensePlateInput/MultiLicensePlateInput';

const { AccordionContainer, LoggedInContainer, TopSpacing } = styles;

interface CarRegistrationAccordionProps {
  formik: any;
  isSignUp: boolean;
  submitButtonText?: string;
  additionalTerms?: string;
  isLoading: boolean;
  isLoadingExistingCard: boolean;
  toggleForm: () => void;
  allowMultipleCars: boolean;
  paymentSessionId?: string;
  currentStep: CampaignSteps;
  setCurrentStep: Dispatch<React.SetStateAction<CampaignSteps>>;
  userName: string;
  userEmail: string;
  logOut: () => void;
  phoneNumberPrefix: string;
  resetAuth: () => void;
  authError: FetchBaseQueryError | undefined;
  isLoggedIn: boolean;
  hasCard: boolean;
  membershipIds: string[];
}

const CarRegistrationAccordion: React.FC<CarRegistrationAccordionProps> = ({
  formik,
  isSignUp,
  submitButtonText = '',
  additionalTerms = '',
  isLoading,
  isLoadingExistingCard,
  toggleForm,
  allowMultipleCars,
  paymentSessionId,
  currentStep,
  setCurrentStep,
  userName,
  userEmail,
  logOut,
  phoneNumberPrefix,
  resetAuth,
  authError,
  isLoggedIn,
  hasCard,
  membershipIds,
}) => {
  /*
   *    HOOKS
   */

  const { t } = useTranslation(['common', 'account', 'campaign']);

  const [hasPaymentError, setHasPaymentError] = useState<boolean>(false);

  /*
   *    STEP MANAGEMENT
   */

  const isStepDisabled = (step: CampaignSteps): boolean => {
    const stepOrder = [
      CampaignSteps.USER_INFORMATION,
      CampaignSteps.PAYMENT_INFORMATION,
    ];

    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(step);
    return stepIndex > currentIndex;
  };

  const isStepCompleted = (step: CampaignSteps): boolean => {
    const stepOrder = [
      CampaignSteps.USER_INFORMATION,
      CampaignSteps.PAYMENT_INFORMATION,
    ];

    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(step);
    return stepIndex < currentIndex;
  };

  const handleStepChange = (step: CampaignSteps) => {
    if (!isStepDisabled(step)) {
      setCurrentStep(step);
    }
  };

  /*
   *    CONDITIONAL RENDERING LOGIC
   */

  const isUserInformationStep = currentStep === CampaignSteps.USER_INFORMATION;

  const isPaymentInformationStep =
    currentStep === CampaignSteps.PAYMENT_INFORMATION;

  const isUserInformationButtonLoading =
    isLoading || formik.isSubmitting || formik.isValidating;

  const showPaymentStep = !(isLoggedIn && hasCard && !hasPaymentError);

  return (
    <div className={AccordionContainer}>
      {/* Step 1: User information - Register or login step */}
      <Accordion
        expanded={isUserInformationStep}
        disabled={!isUserInformationStep}
        sx={{
          '&.Mui-disabled': {
            backgroundColor: 'white',
            color: 'black',
            opacity: 0.8,
            cursor: 'not-allowed',
          },
        }}
      >
        <AccordionSummary
          onClick={() => handleStepChange(CampaignSteps.USER_INFORMATION)}
          expandIcon={
            isStepCompleted(CampaignSteps.USER_INFORMATION) ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ExpandMoreIcon />
            )
          }
          aria-controls="panel2-content"
          id="panel2-header"
          sx={{
            justifyContent: isLoggedIn ? 'space-between' : 'flex-start',
          }}
        >
          {isStepCompleted(CampaignSteps.USER_INFORMATION) && (
            <Typography component="span" variant="h6" fontWeight={900}>
              {t('membership', { ns: 'common' })}
            </Typography>
          )}

          {!isLoggedIn && (
            <Typography component="span" variant="h6" fontWeight={900}>
              {t('becomeMember', { ns: 'common' })}
            </Typography>
          )}

          {isLoggedIn && (
            <div className={LoggedInContainer}>
              <Typography component="span">
                <b>{t('welcome', { ns: 'common' })}</b>
              </Typography>

              <Typography component="span">
                {t('loggedInAs', {
                  user: userName ?? userEmail,
                  ns: 'campaign',
                })}
              </Typography>

              <div>
                <Button
                  appearance="text"
                  size="small"
                  type="button"
                  onClick={logOut}
                  disabled={!isUserInformationStep}
                >
                  {t('logout', { ns: 'common' })}
                </Button>
              </div>
            </div>
          )}
        </AccordionSummary>

        {isUserInformationStep && (
          <AccordionDetails>
            {
              <form onSubmit={formik.handleSubmit}>
                {isLoggedIn ? (
                  <Fragment>
                    {allowMultipleCars ? (
                      <MultiLicensePlateInput
                        name="licensePlate"
                        inputLabel={t('plate', { ns: 'common' })}
                        formik={formik}
                        isCampaign
                        additionalTerms={additionalTerms}
                        allowMultipleCars={allowMultipleCars}
                        helperText={t('lpHelperText', { ns: 'campaign' })}
                      />
                    ) : (
                      <LicensePlateInput
                        name="licensePlate"
                        inputLabel={t('plate', { ns: 'common' })}
                        formik={formik}
                        isCampaign
                        additionalTerms={additionalTerms}
                        keepAsArray
                      />
                    )}

                    <Button
                      appearance="primary"
                      size="large"
                      fullWidth
                      type="submit"
                      hasArrow={false}
                      isLoading={
                        isUserInformationButtonLoading || isLoadingExistingCard
                      }
                      className={TopSpacing}
                    >
                      {submitButtonText}
                    </Button>
                  </Fragment>
                ) : (
                  <Fragment>
                    {isSignUp ? (
                      <CampaignSignUp
                        formik={formik}
                        isLoading={isLoading}
                        phoneNumberPrefix={phoneNumberPrefix}
                        toggleForm={toggleForm}
                        submitButtonLabel={submitButtonText}
                        additionalTerms={additionalTerms}
                        allowMultipleCars={allowMultipleCars}
                        isCarRegistration
                      />
                    ) : (
                      <CampaignLogin
                        formik={formik}
                        authError={authError}
                        toggleForm={toggleForm}
                        resetAuth={resetAuth}
                      />
                    )}
                  </Fragment>
                )}
              </form>
            }
          </AccordionDetails>
        )}
      </Accordion>

      {/* Step 2: Payment step (If user doesn't already have a card) */}
      {showPaymentStep && (
        <Accordion
          expanded={isPaymentInformationStep}
          disabled={
            isStepDisabled(CampaignSteps.PAYMENT_INFORMATION) ||
            !paymentSessionId
          }
          sx={{
            '&.Mui-disabled': {
              backgroundColor: 'white',
              color: 'black',
              opacity: 0.8,
              cursor: 'not-allowed',
            },
          }}
        >
          <AccordionSummary
            onClick={() => handleStepChange(CampaignSteps.PAYMENT_INFORMATION)}
            expandIcon={
              isStepCompleted(CampaignSteps.PAYMENT_INFORMATION) ? (
                <CheckCircleIcon color="success" />
              ) : (
                <ExpandMoreIcon />
              )
            }
            aria-controls="panel3-content"
            id="panel3-header"
          >
            <Typography component="span" variant="h6" fontWeight={900}>
              {t('paymentMethod', { ns: 'campaign' })}
            </Typography>
          </AccordionSummary>

          {isPaymentInformationStep && (
            <AccordionDetails>
              <AdyenDropin
                sessionId={paymentSessionId as string}
                onError={setHasPaymentError}
                campaignMembershipIds={membershipIds}
                skipMembershipValidation
              />
            </AccordionDetails>
          )}
        </Accordion>
      )}
    </div>
  );
};

export default CarRegistrationAccordion;
